import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { 
  ArrowLeft,
  Trophy,
  Award,
  Star,
  Target,
  Zap,
  TrendingUp,
  BarChart3,
  Flame,
  Calendar,
  BookOpen
} from "lucide-react";
import { motion } from "framer-motion";

const badgeIcons = {
  trophy: Trophy,
  award: Award,
  star: Star,
  target: Target,
  zap: Zap
};

export default function StudentAnalytics() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/student`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      A: "text-green-600 bg-green-100",
      B: "text-blue-600 bg-blue-100",
      C: "text-yellow-600 bg-yellow-100",
      D: "text-orange-600 bg-orange-100",
      F: "text-red-600 bg-red-100"
    };
    return colors[grade] || "text-gray-600 bg-gray-100";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="student-analytics">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/student")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-foreground font-['Manrope']">My Analytics</h1>
              <p className="text-sm text-muted-foreground">Track your academic progress</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{analytics?.overview?.average_percentage || 0}%</p>
                    <p className="text-xs text-muted-foreground">Average Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{analytics?.overview?.total_quizzes || 0}</p>
                    <p className="text-xs text-muted-foreground">Quizzes Taken</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Flame className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{analytics?.streak?.current_streak || 0}</p>
                    <p className="text-xs text-muted-foreground">Day Streak</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{analytics?.badges?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Badges Earned</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-muted/50 p-1 rounded-lg">
            <TabsTrigger value="overview" className="rounded-md" data-testid="tab-overview">
              <TrendingUp className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="badges" className="rounded-md" data-testid="tab-badges">
              <Award className="w-4 h-4 mr-2" />
              Badges
            </TabsTrigger>
            <TabsTrigger value="classes" className="rounded-md" data-testid="tab-classes">
              <BookOpen className="w-4 h-4 mr-2" />
              By Class
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Grade Distribution */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-['Manrope']">Grade Distribution</CardTitle>
                  <CardDescription>Breakdown of your quiz grades</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(analytics?.grade_distribution || {}).map(([grade, count]) => {
                      const total = Object.values(analytics?.grade_distribution || {}).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? (count / total) * 100 : 0;
                      return (
                        <div key={grade} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className={`font-medium px-2 py-0.5 rounded ${getGradeColor(grade)}`}>
                              {grade}
                            </span>
                            <span className="text-muted-foreground">{count} quizzes ({percentage.toFixed(0)}%)</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Performance Trend */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-['Manrope']">Recent Performance</CardTitle>
                  <CardDescription>Your last 10 quiz scores</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics?.performance_trend?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No quiz history yet</p>
                  ) : (
                    <div className="space-y-3">
                      {analytics?.performance_trend?.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                          <div>
                            <p className="font-medium text-foreground text-sm">{item.quiz_title}</p>
                            <p className="text-xs text-muted-foreground">{item.class_name}</p>
                          </div>
                          <div className={`text-lg font-bold ${
                            item.percentage >= 80 ? "text-green-600" :
                            item.percentage >= 60 ? "text-yellow-600" : "text-red-600"
                          }`}>
                            {item.percentage}%
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Streak Info */}
              <Card className="border-border/50 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    Study Streak
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 rounded-xl bg-orange-50 border border-orange-100">
                      <p className="text-3xl font-bold text-orange-600">{analytics?.streak?.current_streak || 0}</p>
                      <p className="text-sm text-orange-700">Current Streak</p>
                    </div>
                    <div className="p-4 rounded-xl bg-purple-50 border border-purple-100">
                      <p className="text-3xl font-bold text-purple-600">{analytics?.streak?.longest_streak || 0}</p>
                      <p className="text-sm text-purple-700">Longest Streak</p>
                    </div>
                    <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                      <p className="text-3xl font-bold text-blue-600">{analytics?.streak?.total_days_active || 0}</p>
                      <p className="text-sm text-blue-700">Days Active</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Your Badges</CardTitle>
                <CardDescription>Achievements you've earned</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics?.badges?.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No badges earned yet</p>
                    <p className="text-sm text-muted-foreground">Keep taking quizzes to earn badges!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analytics?.badges?.map((badge, index) => {
                      const IconComponent = badgeIcons[badge.icon] || Award;
                      return (
                        <motion.div
                          key={badge.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-4 rounded-xl border border-border/50 bg-gradient-to-br from-primary/5 to-transparent"
                          data-testid={`badge-${badge.id}`}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                              <IconComponent className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground">{badge.name}</p>
                              {badge.count && (
                                <p className="text-xs text-primary font-medium">×{badge.count}</p>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">{badge.description}</p>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* By Class Tab */}
          <TabsContent value="classes">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Performance by Class</CardTitle>
                <CardDescription>How you're doing in each class</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics?.class_performance?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No class data yet</p>
                ) : (
                  <div className="space-y-4">
                    {analytics?.class_performance?.map((cls, index) => (
                      <motion.div
                        key={cls.class_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 rounded-xl border border-border/50 hover:border-primary/30 transition-all"
                        data-testid={`class-stats-${cls.class_id}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-foreground">{cls.class_name}</h3>
                            <p className="text-sm text-muted-foreground">{cls.subject}</p>
                          </div>
                          <div className={`text-2xl font-bold ${
                            cls.average_score >= 80 ? "text-green-600" :
                            cls.average_score >= 60 ? "text-yellow-600" : "text-red-600"
                          }`}>
                            {cls.average_score}%
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{cls.quizzes_taken} quizzes taken</span>
                        </div>
                        <Progress value={cls.average_score} className="h-2 mt-3" />
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
